import type { TFunction } from 'i18next';
import { Check } from 'lucide-react';

import { Badge } from '@/components/ui/badge';

import type {
  ReceiveHistoryTableData,
  ReceiveQuantityDto,
  ReceiveTemperatureRangeDto,
  ReceiveDangerousGoodDto,
  ReceiveSubprojectDto
} from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';
import type { ColumnPreset, DataTableColumn } from '@/shared/ui/data-table';

const EMPTY_CELL = '-';

/** Per-line quantities as stacked `lineReference: qty unit` rows. */
const renderQuantities = (quantities: ReceiveQuantityDto[] | undefined) => {
  if (!quantities?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {quantities.map((quantity, index) => (
        <span
          key={`${quantity.lineReference}-${index}`}
          className="whitespace-nowrap"
          title={quantity.productDescription}>
          {quantity.lineReference}: {quantity.quantity} {quantity.unit}
        </span>
      ))}
    </div>
  );
};

const renderTemperatureRanges = (ranges: ReceiveTemperatureRangeDto[] | undefined) => {
  if (!ranges?.length) return EMPTY_CELL;

  return ranges.map((range) => `${range.minTemp}°C – ${range.maxTemp}°C`).join('; ');
};

const renderDangerousGoods = (goods: ReceiveDangerousGoodDto[] | undefined) => {
  if (!goods?.length) return EMPTY_CELL;

  return (
    <div className="flex flex-col gap-0.5">
      {goods.map((good, index) => (
        <span key={`${good.unId}-${index}`} className="whitespace-nowrap" title={good.classDescription}>
          {good.unId}
          {good.description ? ` — ${good.description}` : ''}
        </span>
      ))}
    </div>
  );
};

const renderSubprojects = (subprojects: ReceiveSubprojectDto[] | undefined) => {
  if (!subprojects?.length) return EMPTY_CELL;

  return subprojects.map((subproject) => subproject.subprojectCode || subproject.description || EMPTY_CELL).join(', ');
};

/**
 * Receive History columns — JSF parity: the default-visible set and order
 * match the legacy screen; the detail ("disposition") columns are hidden by
 * default and surfaced via Customize Columns / the Medical View preset.
 */
export const getReceiveHistoryColumns = (t: TFunction): DataTableColumn<ReceiveHistoryTableData>[] => [
  {
    id: 'receiveDate',
    accessorKey: 'receiveDate',
    header: t('receive.receivedate.label', 'Actual Date of Receipt'),
    description: 'Actual date of receipt',
    width: 'min-w-[110px]',
    // SR Created column styling; formatDisplayDate keeps the shared app format.
    cell: (row) => <div className="text-xs text-muted-foreground">{formatDisplayDate(row.receiveDate)}</div>,
    enableSorting: true,
    sortFieldId: 'receiveDate'
  },
  {
    id: 'consignmentNumber',
    accessorKey: 'consignmentNumber',
    header: t('journey.participatingRequestsStr.label', 'Consignments'),
    description: 'Consignment (CMR) number',
    width: 'min-w-[130px]',
    cell: (row) => <span className="font-mono text-sm">{row.consignmentNumber}</span>,
    enableSorting: true,
    sortFieldId: 'consignmentNumber'
  },
  {
    id: 'receivedAt',
    accessorKey: 'receivedAt',
    header: t('receive.receivedat.label', 'Received at Location'),
    description: 'Destination location',
    width: 'min-w-[200px] max-w-[300px] whitespace-normal',
    cell: (row) => row.receivedAt || EMPTY_CELL
  },
  {
    id: 'cancellable',
    accessorKey: 'cancellable',
    header: t('receive.canellable.short', 'Cancellable'),
    description: t('receive.canellable.desc', 'This receive can be cancelled'),
    width: 'min-w-[90px]',
    cell: (row) => (row.cancellable ? <Check className="h-4 w-4" aria-hidden /> : EMPTY_CELL)
  },
  {
    id: 'receiveKind',
    accessorKey: 'receiveKind',
    header: t('journeyType.description.label', 'Journey Type'),
    description: 'Internal transfer or customer delivery',
    width: 'min-w-[120px]',
    cell: (row) =>
      row.receiveKind === 'CUSTOMER'
        ? t('receive.customer.label', 'Customer Delivery')
        : t('receive.internal.label', 'Internal Transfer')
  },
  {
    id: 'convoyName',
    accessorKey: 'convoyName',
    header: t('convoy.name.desc', 'Convoy'),
    description: 'Convoy',
    width: 'min-w-[120px]',
    cell: (row) => row.convoyName || EMPTY_CELL
  },
  {
    id: 'vehicleType',
    accessorKey: 'vehicleType',
    header: t('journey.vehicleType.label', 'Vehicle Type'),
    description: 'Vehicle type',
    width: 'min-w-[120px]',
    cell: (row) => row.vehicleType || EMPTY_CELL
  },
  {
    id: 'transportCompany',
    accessorKey: 'transportCompany',
    header: t('journey.transportCompany.label', 'Transport Company'),
    description: 'Transport company',
    width: 'min-w-[150px]',
    cell: (row) => row.transportCompany || EMPTY_CELL
  },
  {
    id: 'comments',
    header: t('receive.comments.label', 'Comments'),
    description: 'Shipment remark + delivery comments',
    width: 'min-w-[160px]',
    cell: (row) =>
      row.comments?.length ? (
        <div className="flex flex-wrap gap-1">
          {row.comments.map((comment, index) => (
            <Badge key={index} variant="outline" className="max-w-[240px]" title={comment.text}>
              <span className="truncate">{comment.text}</span>
            </Badge>
          ))}
        </div>
      ) : (
        EMPTY_CELL
      )
  },
  {
    id: 'documentNumber',
    accessorKey: 'documentNumber',
    header: t('receive.document.label', 'Transport Documentation Number'),
    description: 'Reception document / waybill number',
    width: 'min-w-[150px]',
    cell: (row) => row.documentNumber || EMPTY_CELL
  },
  {
    id: 'totalReceivedWeight',
    accessorKey: 'totalReceivedWeight',
    header: t('consignment.columns.weightKg', 'Weight (kg)'),
    description: 'Total received weight in kilograms',
    width: 'min-w-[100px]',
    cell: (row) => (typeof row.totalReceivedWeight === 'number' ? row.totalReceivedWeight.toFixed(2) : EMPTY_CELL)
  },
  {
    id: 'totalReceivedVolume',
    accessorKey: 'totalReceivedVolume',
    header: t('consignment.columns.volumeM3', 'Volume (m³)'),
    description: 'Total received volume in cubic meters',
    width: 'min-w-[100px]',
    cell: (row) => (typeof row.totalReceivedVolume === 'number' ? row.totalReceivedVolume.toFixed(2) : EMPTY_CELL)
  },

  /* --- disposition-detail columns (hidden by default, JSF parity) --------- */
  {
    id: 'inventoryQuantities',
    header: t('tracking.servicereq.unitqty.label', 'Inventory Unit Quantity'),
    description: 'Per-line inventory-unit (LTU) quantities',
    width: 'min-w-[180px]',
    cell: (row) => renderQuantities(row.inventoryQuantities),
    visible: false
  },
  {
    id: 'bundleQuantities',
    header: t('tracking.servicereq.handlingqty.label', 'Handling Unit Quantity'),
    description: 'Per-line bundle quantities',
    width: 'min-w-[180px]',
    cell: (row) => renderQuantities(row.bundleQuantities),
    visible: false
  },
  {
    id: 'reportingQuantities',
    header: t('tracking.servicereq.reportingqty.label', 'Reporting Unit Quantity'),
    description: 'Per-line reporting quantities',
    width: 'min-w-[180px]',
    cell: (row) => renderQuantities(row.reportingQuantities),
    visible: false
  },
  {
    id: 'expiryDates',
    header: t('requestline.expiryDate.label', 'Expiry Date'),
    description: 'Distinct expiry dates across received lines',
    width: 'min-w-[120px]',
    cell: (row) =>
      row.expiryDates?.length ? (
        <div className="text-xs text-muted-foreground">
          {row.expiryDates.map((date) => formatDisplayDate(date)).join(', ')}
        </div>
      ) : (
        EMPTY_CELL
      ),
    visible: false
  },
  {
    id: 'temperatureRanges',
    header: t('tracking.servicereq.temprange.label', 'Temperature Range'),
    description: 'Distinct cold-storage temperature ranges',
    width: 'min-w-[140px]',
    cell: (row) => renderTemperatureRanges(row.temperatureRanges),
    visible: false
  },
  {
    id: 'batchNumbers',
    header: t('requestline.batchNumber.label', 'Batch Number'),
    description: 'Distinct batch numbers across received lines',
    width: 'min-w-[130px]',
    cell: (row) => (row.batchNumbers?.length ? row.batchNumbers.join(', ') : EMPTY_CELL),
    visible: false
  },
  {
    id: 'senderOrganization',
    accessorKey: 'senderOrganization',
    header: t('newcmr.senderorg.label', 'Sender Organization'),
    description: 'Sender organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.senderOrganization || EMPTY_CELL,
    visible: false
  },
  {
    id: 'receiverOrganization',
    accessorKey: 'receiverOrganization',
    header: t('newcmr.receiverorg.label', 'Receiver Organization'),
    description: 'Receiver organization',
    width: 'min-w-[150px] max-w-[250px] whitespace-normal',
    cell: (row) => row.receiverOrganization || EMPTY_CELL,
    visible: false
  },
  {
    id: 'dangerousGoods',
    header: t('requestline.dangerousGoodsUnid.label', 'Dangerous Goods UN Identification number'),
    description: 'Distinct dangerous goods across received lines',
    width: 'min-w-[180px]',
    cell: (row) => renderDangerousGoods(row.dangerousGoods),
    visible: false
  },
  {
    id: 'ownersReference',
    accessorKey: 'ownersReference',
    header: t('request.ownersRef.label', "Owner's Reference"),
    description: "Owner's reference",
    width: 'min-w-[130px]',
    cell: (row) => row.ownersReference || EMPTY_CELL,
    visible: false
  },
  {
    id: 'origin',
    accessorKey: 'origin',
    header: t('journey.origin.label', 'Origin Location'),
    description: 'Origin location (parent shipment)',
    width: 'min-w-[180px] max-w-[280px] whitespace-normal',
    cell: (row) => row.origin || EMPTY_CELL,
    visible: false
  },
  {
    id: 'totalValue',
    accessorKey: 'totalValue',
    header: t('tracking.servicereq.totval.label', 'Total Value (USD)'),
    description: 'Total consignment-line value',
    width: 'min-w-[120px]',
    cell: (row) => (typeof row.totalValue === 'number' ? row.totalValue.toLocaleString() : EMPTY_CELL),
    visible: false
  },
  {
    id: 'subprojects',
    header: t('subproject.idnf.label', 'Sub-project code'),
    description: 'Distinct subprojects of the parent shipment',
    width: 'min-w-[130px]',
    cell: (row) => renderSubprojects(row.subprojects),
    visible: false
  }
];

/** Column presets — Medical View mirrors the JSF preset composition. */
export const getReceiveHistoryColumnPresets = (t: TFunction): ColumnPreset[] => [
  {
    id: 'medical-view',
    label: t('gen.customize.medview.button', 'Medical View'),
    columnIds: [
      'receiveDate',
      'consignmentNumber',
      'receivedAt',
      'cancellable',
      'documentNumber',
      'expiryDates',
      'temperatureRanges',
      'batchNumbers',
      'receiverOrganization',
      'totalValue'
    ]
  }
];
